const PLACEHOLDER_REGEX = /\{\{\s*([A-Za-z_][A-Za-z0-9_]*)\s*\}\}/g;
const QUOTED_PLACEHOLDER_REGEX = /'{1,2}\{\{\s*([A-Za-z_][A-Za-z0-9_]*)\s*\}\}'{1,2}/g;
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATETIME_REGEX = /^\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z)?$/;
const NUMBER_REGEX = /^-?\d+(?:\.\d+)?$/;
const VALID_TYPES = ['date', 'datetime-local', 'number', 'text', 'tags', 'tags_any', 'tags_all'];
const RELATIVE_DATE_REGEX = /^(\d+)\s+(day|days|week|weeks|month|months|year|years)\s+(ago|from now)$/i;
const VARIABLE_LINE_PREFIX_REGEX = /^\s*--\s*@([A-Za-z_][A-Za-z0-9_]*)(?::(.+))?\s*$/;
const TYPE_SPEC_REGEX = /^([A-Za-z_][A-Za-z0-9_-]*)(?::([A-Za-z_][A-Za-z0-9_]*)(?:\((.+)\))?)?$/;

function parseVariableDeclarationLine(line) {
  const trimmedLine = String(line || '').trim();
  let depth = 0;
  let assignmentIndex = -1;

  for (let index = 0; index < trimmedLine.length; index += 1) {
    const character = trimmedLine[index];

    if (character === '(') {
      depth += 1;
    } else if (character === ')') {
      depth = Math.max(0, depth - 1);
    } else if (character === '=' && depth === 0) {
      assignmentIndex = index;
      break;
    }
  }

  if (assignmentIndex === -1) {
    return null;
  }

  const leftSide = trimmedLine.slice(0, assignmentIndex).trim();
  const literal = trimmedLine.slice(assignmentIndex + 1).trim();
  const match = leftSide.match(VARIABLE_LINE_PREFIX_REGEX);

  if (!match || literal === '') {
    return null;
  }

  return {
    name: match[1],
    explicitTypeSpec: match[2] ? match[2].trim() : null,
    literal,
  };
}

function padNumber(value) {
  return String(value).padStart(2, '0');
}

function isValidDateValue(date) {
  return date instanceof Date && !Number.isNaN(date.getTime());
}

function formatUtcSqlDateTime(date) {
  if (!isValidDateValue(date)) {
    return '';
  }

  return `${date.getUTCFullYear()}-${padNumber(date.getUTCMonth() + 1)}-${padNumber(date.getUTCDate())} ${padNumber(date.getUTCHours())}:${padNumber(date.getUTCMinutes())}:${padNumber(date.getUTCSeconds())}`;
}

function startOfLocalDay(date) {
  const nextDate = new Date(date);
  nextDate.setHours(0, 0, 0, 0);
  return nextDate;
}

function addToLocalDate(date, amount, unit) {
  const nextDate = new Date(date);

  if (unit === 'day' || unit === 'days') {
    nextDate.setDate(nextDate.getDate() + amount);
  } else if (unit === 'week' || unit === 'weeks') {
    nextDate.setDate(nextDate.getDate() + (amount * 7));
  } else if (unit === 'month' || unit === 'months') {
    nextDate.setMonth(nextDate.getMonth() + amount);
  } else if (unit === 'year' || unit === 'years') {
    nextDate.setFullYear(nextDate.getFullYear() + amount);
  }

  return nextDate;
}

function resolveRelativeDateExpression(value, type) {
  const normalized = String(value || '').trim().toLowerCase();
  const now = new Date();

  if (normalized === 'today') {
    return type === 'date' ? startOfLocalDay(now) : now;
  }

  if (normalized === 'yesterday') {
    const date = addToLocalDate(now, -1, 'day');
    return type === 'date' ? startOfLocalDay(date) : date;
  }

  if (normalized === 'tomorrow') {
    const date = addToLocalDate(now, 1, 'day');
    return type === 'date' ? startOfLocalDay(date) : date;
  }

  const relativeMatch = normalized.match(RELATIVE_DATE_REGEX);

  if (!relativeMatch) {
    return null;
  }

  const amount = Number(relativeMatch[1]);
  const unit = relativeMatch[2].toLowerCase();
  const direction = relativeMatch[3].toLowerCase() === 'ago' ? -1 : 1;
  const date = addToLocalDate(now, amount * direction, unit);

  return type === 'date' ? startOfLocalDay(date) : date;
}

function utcLiteralToLocalDateInput(value) {
  const date = new Date(value);

  if (!isValidDateValue(date)) {
    return value;
  }

  return `${date.getFullYear()}-${padNumber(date.getMonth() + 1)}-${padNumber(date.getDate())}`;
}

function utcLiteralToLocalDateTimeInput(value) {
  const date = new Date(value);

  if (!isValidDateValue(date)) {
    const normalized = String(value).replace(' ', 'T').replace(/Z$/, '');
    return normalized.length >= 16 ? normalized.slice(0, 16) : normalized;
  }

  return `${date.getFullYear()}-${padNumber(date.getMonth() + 1)}-${padNumber(date.getDate())}T${padNumber(date.getHours())}:${padNumber(date.getMinutes())}`;
}

function localDateObjectToDateInputValue(date) {
  if (!isValidDateValue(date)) {
    return '';
  }

  return `${date.getFullYear()}-${padNumber(date.getMonth() + 1)}-${padNumber(date.getDate())}`;
}

function localDateObjectToDateTimeInputValue(date) {
  if (!isValidDateValue(date)) {
    return '';
  }

  return `${date.getFullYear()}-${padNumber(date.getMonth() + 1)}-${padNumber(date.getDate())}T${padNumber(date.getHours())}:${padNumber(date.getMinutes())}`;
}

function localDateInputToUtcLiteral(value) {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (!match) {
    return value;
  }

  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    0,
    0,
    0,
    0,
  );

  return isValidDateValue(date) ? formatUtcSqlDateTime(date) : value;
}

function localDateTimeInputToUtcLiteral(value) {
  const match = String(value || '').match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/,
  );

  if (!match) {
    return value;
  }

  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    Number(match[4]),
    Number(match[5]),
    Number(match[6] || 0),
    0,
  );

  return isValidDateValue(date) ? formatUtcSqlDateTime(date) : value;
}

function inferTypeFromLiteral(literal) {
  const trimmed = (literal || '').trim();
  const quotedMatch = trimmed.match(/^'(.*)'$/s);
  const value = quotedMatch ? quotedMatch[1].replace(/''/g, '\'') : trimmed;

  if (quotedMatch && ISO_DATE_REGEX.test(value)) {
    return 'date';
  }

  if (quotedMatch && ISO_DATETIME_REGEX.test(value)) {
    return 'datetime-local';
  }

  if (NUMBER_REGEX.test(trimmed)) {
    return 'number';
  }

  return 'text';
}

function normalizeInputValue(type, value) {
  if (value == null) {
    return '';
  }

  if (type === 'tags' || type === 'tags_any' || type === 'tags_all') {
    return String(value)
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  if (type === 'datetime-local') {
    const normalized = String(value).replace(' ', 'T').replace(/Z$/, '');
    return normalized.length >= 16 ? normalized.slice(0, 16) : normalized;
  }

  return String(value);
}

function literalToInputValue(literal, type) {
  const trimmed = (literal || '').trim();
  const quotedMatch = trimmed.match(/^'(.*)'$/s);
  const value = quotedMatch ? quotedMatch[1].replace(/''/g, '\'') : trimmed;
  const relativeDate = (type === 'date' || type === 'datetime-local')
    ? resolveRelativeDateExpression(value, type)
    : null;

  if (relativeDate && type === 'date') {
    return localDateObjectToDateInputValue(relativeDate);
  }

  if (relativeDate && type === 'datetime-local') {
    return localDateObjectToDateTimeInputValue(relativeDate);
  }

  if (type === 'date') {
    return ISO_DATE_REGEX.test(value) ? value : utcLiteralToLocalDateInput(value);
  }

  if (type === 'datetime-local') {
    return utcLiteralToLocalDateTimeInput(value);
  }

  return normalizeInputValue(type, value);
}

function tagsValueToStoredLiteral(value) {
  if (Array.isArray(value)) {
    return value
      .map((entry) => {
        if (entry && typeof entry === 'object') {
          return String(entry.label || entry.name || entry.id || '').trim();
        }

        return String(entry || '').trim();
      })
      .filter(Boolean)
      .join(',');
  }

  return String(value || '')
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .join(',');
}

function tagsValueToExecutionLiteral(value) {
  if (Array.isArray(value)) {
    return value
      .map((entry) => {
        if (entry && typeof entry === 'object' && entry.id != null) {
          return String(entry.id).trim();
        }

        return String(entry || '').trim();
      })
      .filter(Boolean)
      .join(',');
  }

  return String(value || '')
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .join(',');
}

function tagsValueToExecutionIds(value) {
  return tagsValueToExecutionLiteral(value)
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function parseTagConfig(configString) {
  if (!configString) {
    return null;
  }

  const entries = String(configString)
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
  const config = {};

  entries.forEach((entry) => {
    const [key, value] = entry.split('=').map((part) => part && part.trim());

    if (!key) {
      return;
    }

    config[key] = value || key;
  });

  return config;
}

function normalizeTypeSpec(explicitTypeSpec, literal) {
  if (!explicitTypeSpec) {
    return {
      type: inferTypeFromLiteral(literal),
      declarationTypeSpec: null,
      tagAlias: null,
      tagConfig: null,
    };
  }

  const match = String(explicitTypeSpec).trim().match(TYPE_SPEC_REGEX);

  if (!match) {
    return {
      type: inferTypeFromLiteral(literal),
      declarationTypeSpec: explicitTypeSpec,
      tagAlias: null,
      tagConfig: null,
    };
  }

  const explicitType = match[1];
  const tagAlias = match[2] || null;
  const tagConfig = parseTagConfig(match[3]);
  let type = explicitType;

  if (explicitType === 'tags') {
    type = 'tags_any';
  } else if (!VALID_TYPES.includes(explicitType)) {
    type = inferTypeFromLiteral(literal);
  }

  return {
    type,
    declarationTypeSpec: explicitTypeSpec,
    tagAlias,
    tagConfig,
  };
}

function inputValueToLiteral(type, value) {
  if (type === 'tags' || type === 'tags_any' || type === 'tags_all') {
    return tagsValueToStoredLiteral(value);
  }

  const safeValue = value == null ? '' : String(value);

  if (type === 'number') {
    return safeValue === '' ? '0' : safeValue;
  }

  let nextValue = safeValue;

  if (type === 'date') {
    nextValue = localDateInputToUtcLiteral(safeValue);
  } else if (type === 'datetime-local') {
    nextValue = localDateTimeInputToUtcLiteral(safeValue);
  }

  const quotedValue = nextValue.replace(/'/g, '\'\'');

  return `'${quotedValue}'`;
}

function variableToExecutionLiteral(variable, runtimeValue) {
  if (variable.type === 'tags_all') {
    const ids = tagsValueToExecutionIds(runtimeValue || variable.value);

    if (ids.length === 0) {
      return '1 = 1';
    }

    const bridgeTable = variable.tagAlias || 'item_tags';
    const tagColumn = variable.tagConfig?.tag_id || 'tag_id';
    const relationEntries = Object.entries(variable.tagConfig || {})
      .filter(([key]) => key !== 'tag_id');
    const [relationColumn, relationExpression] = relationEntries[0] || ['item_id', 'i.id'];

    return ids
      .map((id) => [
        `EXISTS (SELECT 1 FROM ${bridgeTable} it`,
        `WHERE it.${relationColumn} = ${relationExpression}`,
        `AND it.${tagColumn} = ${id})`,
      ].join(' '))
      .join('\nAND ');
  }

  if (variable.type === 'tags_any' || variable.type === 'tags') {
    return tagsValueToExecutionLiteral(runtimeValue || variable.value) || 'NULL';
  }

  return inputValueToLiteral(variable.type, variable.value);
}

function replaceVariableLine(
  lines,
  lineIndex,
  variableName,
  declarationTypeSpec,
  variableType,
  literal,
) {
  const nextLines = [...lines];
  const typeSpec = declarationTypeSpec || variableType;
  nextLines[lineIndex] = `-- @${variableName}:${typeSpec} = ${literal}`;
  return nextLines;
}

export function parseQueryVariables(query) {
  const source = query || '';
  const lines = source.split(/\r?\n/);
  const variables = [];

  for (let index = 0; index < lines.length; index += 1) {
    const declaration = parseVariableDeclarationLine(lines[index]);

    if (!declaration) {
      const trimmedLine = lines[index].trim();

      if (trimmedLine !== '' && !trimmedLine.startsWith('--')) {
        break;
      }
    } else {
      const normalizedTypeSpec = normalizeTypeSpec(
        declaration.explicitTypeSpec,
        declaration.literal,
      );

      variables.push({
        name: declaration.name,
        type: normalizedTypeSpec.type,
        explicitType: declaration.explicitTypeSpec,
        declarationTypeSpec: normalizedTypeSpec.declarationTypeSpec,
        tagAlias: normalizedTypeSpec.tagAlias,
        tagConfig: normalizedTypeSpec.tagConfig,
        literal: declaration.literal,
        value: literalToInputValue(declaration.literal, normalizedTypeSpec.type),
        lineIndex: index,
      });
    }
  }

  return variables;
}

export function buildVariableState(query) {
  const variables = parseQueryVariables(query);
  const state = {};

  variables.forEach((variable) => {
    state[variable.name] = {
      ...variable,
      previewLiteral: variable.literal,
    };
  });

  return state;
}

export function applyVariableValueToQuery(query, variableName, nextValue) {
  const lines = (query || '').split(/\r?\n/);
  const variables = parseQueryVariables(query);
  const variable = variables.find((entry) => entry.name === variableName);

  if (!variable) {
    return query || '';
  }

  const literal = inputValueToLiteral(variable.type, nextValue);
  return replaceVariableLine(
    lines,
    variable.lineIndex,
    variableName,
    variable.declarationTypeSpec,
    variable.type,
    literal,
  ).join('\n');
}

export function renderQueryWithVariables(query, runtimeValues = {}) {
  const variables = parseQueryVariables(query);
  const literalMap = variables.reduce((acc, variable) => ({
    ...acc,
    [variable.name]: variableToExecutionLiteral(variable, runtimeValues[variable.name]),
  }), {});

  return (query || '')
    .replace(QUOTED_PLACEHOLDER_REGEX, (match, variableName) => (
      Object.hasOwn(literalMap, variableName) ? literalMap[variableName] : match
    ))
    .replace(PLACEHOLDER_REGEX, (match, variableName) => (
      Object.hasOwn(literalMap, variableName) ? literalMap[variableName] : match
    ));
}

export function hasQueryVariables(query) {
  return parseQueryVariables(query).length > 0;
}
